import { z } from "zod";

import { NumberBinaryOperator, NumberTernaryOperator, Operator } from "../operators.ts";
import type { IResolvableNumberValue } from "../values.ts";
import { ResolvableNumberValue } from "../values.ts";
import type { IBinaryCondition, ITernaryCondition } from "./common.ts";
import { binaryCondition, ternaryCondition } from "./common.ts";

// Zod value schema for number conditions
const NumberValue = z.union([z.number(), ResolvableNumberValue]);

// Zod schemas with type discriminator
export const NumberBinaryCondition = binaryCondition(z.literal("number"), NumberBinaryOperator, NumberValue);
export type NumberBinaryCondition = z.infer<typeof NumberBinaryCondition>;

export const NumberTernaryCondition = ternaryCondition(z.literal("number"), NumberTernaryOperator, NumberValue);
export type NumberTernaryCondition = z.infer<typeof NumberTernaryCondition>;

export const NumberCondition = z.discriminatedUnion("operator", [
  // Binary operators
  binaryCondition(z.literal("number"), z.literal(Operator.EQ), NumberValue),
  binaryCondition(z.literal("number"), z.literal(Operator.NE), NumberValue),
  binaryCondition(z.literal("number"), z.literal(Operator.LT), NumberValue),
  binaryCondition(z.literal("number"), z.literal(Operator.LTE), NumberValue),
  binaryCondition(z.literal("number"), z.literal(Operator.GT), NumberValue),
  binaryCondition(z.literal("number"), z.literal(Operator.GTE), NumberValue),
  // Ternary operators
  ternaryCondition(z.literal("number"), z.literal(Operator.Between), NumberValue),
  ternaryCondition(z.literal("number"), z.literal(Operator.NotBetween), NumberValue),
]);
export type NumberCondition = z.infer<typeof NumberCondition>;

// Interfaces for complex types (verified to match schemas)
export interface INumberBinaryCondition extends IBinaryCondition<
  | typeof Operator.EQ
  | typeof Operator.NE
  | typeof Operator.LT
  | typeof Operator.LTE
  | typeof Operator.GT
  | typeof Operator.GTE,
  number | IResolvableNumberValue
> {
  type: "number";
}

export interface INumberTernaryCondition extends ITernaryCondition<
  typeof Operator.Between | typeof Operator.NotBetween,
  number | IResolvableNumberValue
> {
  type: "number";
}

// Type compatibility assertions - compile-time check that inferred types match interfaces
type _AssertBinaryCompatible = NumberBinaryCondition extends INumberBinaryCondition ? true : never;
type _AssertTernaryCompatible = NumberTernaryCondition extends INumberTernaryCondition ? true : never;
const _binaryCheck: _AssertBinaryCompatible = true;
const _ternaryCheck: _AssertTernaryCompatible = true;
void _binaryCheck;
void _ternaryCheck;
